import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { MaskLine } from "./Reveal";

const TEXTURE =
  "https://images.unsplash.com/photo-1628965882741-570e75becd5d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGluayUyMG9jaHJlJTIwcGFwZXIlMjB0ZXh0dXJlfGVufDB8fHx8MTc4NTM3NDI4NHww&ixlib=rb-4.1.0&q=85";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yTexture = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const opacityTexture = useTransform(scrollYProgress, [0, 1], [0.22, 0]);
  const yText = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-24 pb-16"
    >
      <motion.img
        src={TEXTURE}
        alt=""
        aria-hidden="true"
        style={{ y: yTexture, opacity: opacityTexture }}
        className="absolute -right-20 top-0 h-[120%] w-[65%] object-cover mix-blend-multiply grayscale pointer-events-none"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-alabaster via-alabaster/60 to-transparent pointer-events-none" />

      <div className="relative max-w-7xl mx-auto w-full px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="font-mono text-xs tracking-[0.35em] uppercase text-ochre mb-8"
        >
          <MaskLine delay={0.1}>Writer · Producer · Storyteller</MaskLine>
        </motion.div>

        <motion.h1
          style={{ y: yText }}
          className="font-serif text-onyx leading-[0.95] text-5xl sm:text-7xl lg:text-8xl max-w-5xl"
        >
          <MaskLine delay={0.15}>Storytelling born</MaskLine>
          <MaskLine delay={0.3} className="italic text-ochre">
            from stone
          </MaskLine>
          <MaskLine delay={0.45}>&amp; earth.</MaskLine>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.9 }}
          className="mt-10 max-w-xl font-sans text-lg leading-relaxed text-onyx-muted"
        >
          The studio developing bold, character-driven film, television and
          documentaries rooted in place, memory and the quiet weight of what we leave behind.
        </motion.p>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="mt-12 flex items-center gap-6"
        >
          <a
            href="#slate"
            data-testid="hero-slate-btn"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector("#slate")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="font-sans uppercase text-xs tracking-[0.2em] bg-onyx text-alabaster px-8 py-4 hover:bg-ochre transition-colors duration-300"
          >
            View the slate
          </a>
          <a
            href="#about"
            data-testid="hero-about-btn"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector("#about")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="link-underline font-sans uppercase text-xs tracking-[0.2em] text-onyx"
          >
            Meet Su
          </a>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-0 right-0 max-w-7xl mx-auto px-6 md:px-10 flex justify-between items-end font-mono text-[10px] tracking-[0.25em] uppercase text-onyx-muted"
      >
        <span>Est. — A Writer-Producer House</span>
        <span className="hidden sm:block">Scroll to explore ↓</span>
      </motion.div>
    </section>
  );
}
